<script setup lang="ts">
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  showFieldLabel,
  getCaretOffsetWithin,
  setCaretOffsetWithin,
  handleLabelTabNavigation,
} from "@/util/methods";
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  element: {
    type: Object,
    default: () => ({}),
  },
  formLabelVisible: Boolean,
});

const emit = defineEmits(["update:label", "update:placeholder", "addToHistory"]);
const store = useAppStore();

const getInputCSSList = (): Array<String> => {
  return store.builderConfig.inputStyleType === "line" ? ["line-form-style"] : [];
};

const editableLabel = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLElement | null>(null);
const localLabel = ref(props.element.label || "");
const initialLabel = ref(props.element.label || "");
const inputClicked = ref(false);
const isLabelFocused = ref(false);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.element.label,
  (newLabel) => {
    localLabel.value = newLabel || "";
    initialLabel.value = newLabel || "";
  }
);

const handleInputClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!inputClicked.value) {
    // Ensure a text node exists to allow caret rendering when empty
    let textNode = target.firstChild;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      const node = document.createTextNode(target.textContent || "");
      target.textContent = "";
      target.appendChild(node);
      textNode = node;
    }

    // Select all text within on first click
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.setEnd(textNode, (textNode.textContent || "").length);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    inputClicked.value = true;
  }
  // For subsequent clicks, let browser place the caret
};

const handleInput = () => {
  emit("update:label", localLabel.value);
};

const onLabelBlur = () => {
  isLabelFocused.value = false;
  if (initialLabel.value !== localLabel.value) {
    emit("addToHistory");
    initialLabel.value = localLabel.value;
  }
  inputClicked.value = false;
};

const inputPlaceholder = ref(props.element.placeholder || "");
const placeholderInputElement = ref<HTMLInputElement | null>(null);
watch(
  () => props.element.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal || "";
  }
);

const onPlaceholderFocus = () => {
  placeholderInputValue.value = inputPlaceholder.value;
  nextTick(() => {
    try {
      const el = placeholderInputElement.value;
      el?.select?.();
    } catch (error) {
      console.error("Failed to select address placeholder on focus", error);
    }
  });
};

const onPlaceholderInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  placeholderInputValue.value = target.value;
  inputPlaceholder.value = target.value;
};

const onPlaceholderBlur = (event: Event) => {
  placeholderInputValue.value = "";
  emit("update:placeholder", inputPlaceholder.value);
  emit("addToHistory");
};

const placeholderInputValue = ref("");

const handleFocus = (event: FocusEvent) => {
  isLabelFocused.value = true;
  const target = event.target as HTMLElement;
  if (target.isContentEditable) {
    const range = document.createRange();
    range.selectNodeContents(target);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};
</script>

<template>
  <div
    v-show="showFieldLabel(formLabelVisible, element)"
    style="display: inline-flex"
    class="label-wrapper"
  >
    <div class="flex items-start">
      <div
        v-show="formLabelVisible"
        class="label-container"
        :data-active="isLabelFocused ? 'true' : 'false'"
      >
        <div
          ref="inputElement"
          contenteditable="true"
          class="label-input font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:border-gray-300 focus:ring-0"
          :data-placeholder="t('common.labelPlaceholder')"
          :data-address-tag="element?.tag || ''"
          @input="
            (e) => {
              const target = e.target as HTMLElement;
              const caret = getCaretOffsetWithin(target);
              const text = (target.textContent || '').replace(/\u200B/g, '');
              localLabel = text;
              handleInput();
              handleSafariCursor(e);
              nextTick(() => setCaretOffsetWithin(target, caret));
            }
          "
          @keydown="(e) => { if (!handleLabelTabNavigation(e as KeyboardEvent)) handleSafariCursor(e); }"
          @blur="onLabelBlur"
          @click="
            (e) => {
              e.stopPropagation();
              handleInputClick(e);
            }
          "
          @focus="
            (e) => {
              e.stopPropagation();
              isLabelFocused = true;
            }
          "
          v-text="localLabel"
        ></div>
        <span v-if="element.required && formLabelVisible" class="required-asterisk">*</span>
      </div>
      <div v-if="element.hidden">
        <small class="ml-[5px] rounded-sm bg-[#e1e1e1] px-[5px] py-[2px]">
          {{ t("common.hidden") }}
        </small>
      </div>
    </div>
  </div>

  <input
    ref="placeholderInputElement"
    :value="placeholderInputValue"
    :placeholder="inputPlaceholder"
    class="form-placeholder form-control placeholder-input"
    :class="getInputCSSList()"
    :style="{
      fontSize: store.fieldStyle.placeholderFontSize + 'px',
    }"
    @focus="onPlaceholderFocus"
    @input="onPlaceholderInput"
    @blur="onPlaceholderBlur"
  />
  <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
    {{ element.shortLabel }}
  </span>
</template>

<style scoped>
.label-wrapper {
  display: flex;
}

.required-flag {
  display: flex;
  margin-bottom: 6px;
}

.label-container {
  display: inline;
  width: auto;
  overflow: visible;
  position: relative;
}

.label-input {
  display: inline;
  min-width: 0;
  max-width: 100%;
  outline: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  margin-bottom: 2px;
  word-break: break-word;
  min-height: 24px;
  position: relative; /* allow fake caret positioning */
}

.label-input:focus {
  background-color: rgba(200, 200, 200, 0.1);
  border-radius: 2px;
  outline: none;
}

.required-asterisk {
  font-weight: bold;
  padding-left: 0;
  font-size: 14px;
  display: inline;
  margin-left: 4px;
}

.placeholder-input {
  padding: 8px 20px;
  border: 1px solid #acacacff;
  color: #8c8c8c;
}

.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

/* Only show placeholder when element is active */
.label-input:empty:before {
  content: "";
  pointer-events: none; /* ensure placeholder never blocks caret */
}

/* Show placeholder when inside an active element */
*[data-active="true"] .label-input:empty:before,
.label-input:focus:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  font-style: italic;
  pointer-events: none; /* allow clicks to focus contenteditable */
  margin-left: 2px; /* leave room for fake caret */
}

/* Fake caret when focused and empty so caret + placeholder appear together */
.label-input:focus:empty::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0.15em;
  width: 1px;
  height: 1em;
  background: currentColor;
  pointer-events: none;
  animation: blink-caret 1s steps(1, end) infinite;
}

@keyframes blink-caret {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
</style>
